import type { ContainerImageFormFieldMapping } from '@/components-biz/ImageSelect';
import { createSystemImageFieldMappings } from '@/components-biz/ImageSelect';

import { buildImageListQueryFromResource } from '../../utils/imageQuery';
import type { ImageBlockOptions } from './types';

/** 直建 legacy 扁平字段（对齐 image-setting.tsx hiddenFields） */
function buildLegacyFlatImageMappings(imagePrefix: string): ContainerImageFormFieldMapping[] {
  return [
    { field: 'image', getValueField: 'dockerRefUrl' },
    { field: `${imagePrefix}.url`, getValueField: 'dockerRefUrl' },
    { field: `${imagePrefix}.name`, getValueField: 'name' },
    { field: 'imageNickname', getValueField: 'name' },
    {
      field: `${imagePrefix}.architecture`,
      getValueField: 'cpuArchitectures[0]',
    },
    { field: 'architecture', getValueField: 'cpuArchitectures[0]' },
    { field: `${imagePrefix}.os`, getValueField: 'vendor' },
    { field: `${imagePrefix}.version`, getValueField: 'version' },
  ];
}

export function buildImageMappings(
  imagePrefix: string | undefined,
  options?: Pick<ImageBlockOptions, 'legacyFlatFields' | 'extraImageMappings' | 'syncArchitectureFromImage'>,
): ContainerImageFormFieldMapping[] {
  const normalizedPrefix = String(imagePrefix ?? 'basic.image').replace(/\.$/, '');
  const mappings = createSystemImageFieldMappings(normalizedPrefix);
  if (options?.legacyFlatFields !== false) {
    mappings.push(...buildLegacyFlatImageMappings(normalizedPrefix));
  }
  if (options?.extraImageMappings?.length) {
    mappings.push(...options.extraImageMappings);
  }
  const syncArchitecture = options?.syncArchitectureFromImage === true;
  if (!syncArchitecture) {
    return dedupeMappings(mappings).filter((item) => item.field !== 'architecture' && !item.field.endsWith('.architecture'));
  }
  return dedupeMappings(mappings);
}

function dedupeMappings(mappings: ContainerImageFormFieldMapping[]): ContainerImageFormFieldMapping[] {
  const seen = new Set<string>();
  return mappings.filter((item) => {
    if (seen.has(item.field)) return false;
    seen.add(item.field);
    return true;
  });
}

export function buildImageQuery(
  architecture: string | undefined,
  gpu: Record<string, unknown> | undefined,
  downloadType?: string,
): Record<string, unknown> {
  const cpu = architecture != null && String(architecture).trim() !== '' ? String(architecture) : undefined;
  return buildImageListQueryFromResource({ gpu, cpu, downloadType });
}

export function collectHiddenMappingFields(mappings: ContainerImageFormFieldMapping[]): string[] {
  return mappings.map((item) => item.field);
}

/** 镜像对象是否已有有效标识（表单形 key/url/name 与 API 形 imageID 等） */
function imageRecordHasValue(image: Record<string, unknown> | undefined | null): boolean {
  if (!image || typeof image !== 'object') return false;
  return Boolean(
    String(image.key ?? '').trim() ||
      String(image.imageKey ?? '').trim() ||
      String(image.url ?? '').trim() ||
      String(image.image ?? '').trim() ||
      String(image.imageID ?? '').trim() ||
      String(image.imageId ?? '').trim() ||
      String(image.name ?? '').trim() ||
      String(image.imageName ?? '').trim(),
  );
}

/** presetConfig 是否已包含镜像（支持 API spec / 表单 nested / 顶层 image） */
export function hasPresetImage(preset: unknown): boolean {
  if (!preset || typeof preset !== 'object') return false;
  const record = preset as Record<string, unknown>;

  const system = record.system as Record<string, unknown> | undefined;
  if (system) {
    if (
      String(system.imageID ?? '').trim() ||
      String(system.imageId ?? '').trim() ||
      String(system.url ?? '').trim() ||
      String(system.image ?? '').trim() ||
      String(system.name ?? '').trim() ||
      String(system.imageName ?? '').trim()
    ) {
      return true;
    }
  }

  const basic = record.basic as Record<string, unknown> | undefined;
  if (basic && imageRecordHasValue(basic.image as Record<string, unknown> | undefined)) {
    return true;
  }

  // BareMetal 等：表单字段在顶层 image
  if (imageRecordHasValue(record.image as Record<string, unknown> | undefined)) {
    return true;
  }

  return false;
}
